import React, {useCallback, useState} from 'react'
import "./third-onboard-screen.css"
import { Formik } from "formik";
import * as Yup from "yup";
import {
    modalGiftState,
    selectedShippingAddress
} from "@atoms";
import {useRecoilState, useSetRecoilState} from "recoil";
import axios from "axios";
import uniqid from "uniqid";
import {formUploadState} from "../../../Recoil/Atoms";
import { getCartDiscountEvent } from "../../../api/activePromoEvent";
import { mergeDebug } from "@api";
import { onboardingCartFields } from "../../../api/onboardingChoice";
const BASE_URL = process.env.REACT_APP_BASE_URL;

const ThirdOnboardScreen = ({show, onClose, onClickOut, onSkip, wall = false, onDetailsSubmitted = null, step = 2}) => {
    const [checkingGallery, setCheckingGallery] = useState(false); // gallery-merge candidate check in flight
    const [gift,] = useRecoilState(modalGiftState);
    const [uploadState, setUploadState] = useRecoilState(formUploadState);
    const setAddress = useSetRecoilState(selectedShippingAddress);

    const [focus, setFocus] = useState({
        email: false,
        name: false,
        phone: false,
    });

    const currentDate = new Date();
    const idtTimeZone = 'Asia/Jerusalem';
    const options = { timeZone: idtTimeZone };
    const idtDate = new Date(currentDate.toLocaleString('en-US', options));

    const padTwoDigits = (num) => {
        return num.toString().padStart(2, "0");
    }

    const dateFormat = (date, dateDivider = "/") => {
        return (
          [
              padTwoDigits(date.getDate()),
              padTwoDigits(date.getMonth() + 1),
              date.getFullYear().toString().slice(-2),
          ].join(dateDivider) +
          "-" +
          [
              padTwoDigits(date.getHours()),
              padTwoDigits(date.getMinutes()),
              padTwoDigits(date.getSeconds()),
          ].join(":")
        );
    }

    const formattedDate = dateFormat(idtDate);

    // Function to generate a session ID via server API
    const generateSessionId = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/generate-session-id`);
            if (response.data && response.data.sessionId) {
                console.log(`Server generated session ID: ${response.data.sessionId}`);
                return response.data.sessionId;
            } else {
                throw new Error('Invalid response from session generation API');
            }
        } catch (error) {
            console.error('Failed to generate session ID from server:', error.message);
            const chars = '0123456789abcdefghijklmnopqrstuvwxyz';
            let fallbackId = 'b';
            for (let i = 0; i < 5; i++) {
                fallbackId += chars[Math.floor(Math.random() * chars.length)];
            }
            console.log(`Generated fallback session ID: ${fallbackId}`);
            return fallbackId;
        }
    };

    const putData = useCallback(async (data) => {
        try {
            const uniqueUserId = localStorage.getItem("uniqueUserId") || "unknown";
            let sessionId = localStorage.getItem("sessionId");

            if (!sessionId) {
                sessionId = await generateSessionId();
                localStorage.setItem("sessionId", sessionId);
                console.log(`Generated new sessionId: ${sessionId}`);
            }

            const response = await axios.post(BASE_URL + "/user/register", {
                customerName: data.fullName,
                email: data.email,
                selfOrGift: data.selfOrGift,
                agreed: data.agreed,
                date: formattedDate,
                uid: uniqueUserId,
            });

            const cartResponse = await axios.post(BASE_URL + "/addtocart", {
                uid: uniqueUserId,
                sessionId: sessionId,
                userInfo: {
                    fullName: data.fullName,
                    email: data.email,
                    phoneNumber: data.phoneNumber
                },
                frameType: "classic",
                discountEvent: getCartDiscountEvent(),
                ...onboardingCartFields()
            });

            console.log(`[${uniqueUserId}-${data.email}] Onboarding form submitted successfully - Name: ${data.fullName}, Gift: ${data.selfOrGift}, Date: ${formattedDate}`);
            console.log(`[${uniqueUserId}-${data.email}] Cart created/updated with sessionId: ${sessionId}`);
        } catch (err) {
            const uniqueUserId = localStorage.getItem("uniqueUserId") || "unknown";
            console.log(`[${uniqueUserId}-${data.email}] Error during onboarding form submission: ${err.message}`);
            console.log(err);
        }
    }, [formattedDate]);

    const validationSchema = Yup.object({
        fullName:
            Yup.string()
                .min(1, 'שם קצר מדי')
                .max(50, 'שם ארוך מדי')
                .required('שדה חובה'),
        email:
            Yup.string()
                .required('שדה חובה')
                .matches(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/ , "Invalid email"),
        phoneNumber:
            Yup.string()
                .required('שדה חובה')
                .matches(/^0[2-9]\d{7,8}$/, 'מספר טלפון לא תקין')
                .test('phone-number', 'מספר טלפון לא תקין', (value) => {
                    if (!value) {
                        return true;
                    }
                    const prefix = value.substring(0, 2);
                    const length = value.length;
                    if ((prefix === '05' || prefix === '07') && length === 10) {
                        return true;
                    }
                    if ((prefix === '03' || prefix === '04' || prefix === '09' || prefix === '02') && length === 9) {
                        return true;
                    }
                    return false;
                }),
    });

    const handleOnSubmit = (values) => {
        if (values.agreed) {
          values.agreed = "joined";
          if (window.flashy && typeof window.flashy.contacts.createOrUpdate === 'function') {
            const names = values.fullName.trim().split(' ');
            const firstName = names[0];
            const lastName = names.length > 1 ? names.slice(1).join(' ') : '';
            const sessionId = localStorage.getItem("sessionId");

            console.log(`🔥 [FLASHY-DEBUG] Preparing to send data to Flashy for ${values.email}`);
            console.log(`🔍 [FLASHY-DEBUG] Session ID details:`, {
              sessionId,
              length: sessionId?.length,
              startsWithB: sessionId?.startsWith('b'),
              isValidFormat: sessionId ? /^b[0-9a-z]{5}$/.test(sessionId) : false
            });

            const flashyData = {
              "email": values.email,
              "first_name": firstName,
              "last_name": lastName,
              "phone": values.phoneNumber,
              "sessionid": sessionId,
              "lists": {
                20215: true,
                28236: true
              }
            };

            console.log(`📤 [FLASHY-DEBUG] Sending data to Flashy:`, {
              email: flashyData.email,
              sessionid: flashyData.sessionid,
              sessionIdLength: flashyData.sessionid?.length,
              firstName: flashyData.first_name,
              lastName: flashyData.last_name,
              phone: flashyData.phone,
              lists: flashyData.lists
            });

            try {
              window.flashy.contacts.createOrUpdate(flashyData);
              console.log(`✅ [FLASHY-DEBUG] Successfully sent data to Flashy for ${values.email} with sessionId: ${sessionId}`);
            } catch (flashyError) {
              console.error(`❌ [FLASHY-DEBUG] Error sending data to Flashy:`, flashyError);
            }
          } else {
            console.warn(`⚠️ [FLASHY-DEBUG] Flashy not available or createOrUpdate function not found`);
          }
        } else {
          values.agreed = "refused";
        }
        values.selfOrGift = gift;

        let uniqueUserId = localStorage.getItem("uniqueUserId");
        if (!uniqueUserId) {
          uniqueUserId = uniqid();
          localStorage.setItem("uniqueUserId", uniqueUserId);
          console.log(`Generated new uniqueUserId: ${uniqueUserId}`);
        }

        localStorage.setItem("userEmail", values.email);
        localStorage.setItem("fullName", values.fullName);
        localStorage.setItem("userPhone", values.phoneNumber || "");

        console.log(`[${uniqueUserId}-${values.email}] Onboarding form submitted - Name: ${values.fullName}, Gift: ${gift || 'not specified'}`);

        setAddress(prevAddress => {
            if (prevAddress) {
                return {
                    ...prevAddress,
                    email: values.email,
                    fullName: values.fullName,
                    phoneNumber: values.phoneNumber,
                    checkBox: true
                };
            }
            return {
                fullName: values.fullName,
                email: values.email,
                phoneNumber: values.phoneNumber,
                address: "",
                addressDetails: "",
                zipCode: "",
                city: "",
                arrivalInstructions: "",
                checkBox: true
            };
        });

        // Gallery-merge (split-cart recovery): when the parent wants to check for an
        // existing gallery, hand it the values + the pending cart write and let IT
        // finish onboarding (loginCompleted / upload reveal) after the check.
        if (onDetailsSubmitted) {
            mergeDebug('details submitted -> checking for existing galleries (email/phone from form)');
            // Keep this form up (with the submit spinner) while the parent checks
            // for an existing gallery — the modal only closes once we know whether
            // the restore screen replaces it or the upload UI shows.
            setCheckingGallery(true);
            onDetailsSubmitted(values, putData(values)).finally(() => {
                setCheckingGallery(false);
                onClose();
            });
            return;
        }

        putData(values);
        localStorage.setItem('loginCompleted', 1);
        setUploadState(true);
        onClose();
    };

    return (
        <div onClick={onClickOut} className={`obr-overlay ${show ? "show" : ""}`}>
            <div onClick={e => e.stopPropagation()} className="obr-card">
                <div className="obr-ambient"></div>
                <div className="obr-ambient obr-ambient-2"></div>

                <div className="obr-step">
                    <span className="obr-step-num">{step}</span>
                    <span className="obr-step-sep">/</span>
                    <span className="obr-step-total">2</span>
                </div>

                <div className="obr-content">
                    <Formik
                        initialValues={{
                            fullName: "",
                            email: "",
                            phoneNumber: "",
                            selfOrGift: "",
                            agreed: true,
                        }}
                        validateOnChange={false}
                        validateOnBlur={false}
                        validationSchema={validationSchema}
                        onSubmit={(values) => handleOnSubmit(values)}
                    >
                        {({ handleSubmit, handleChange,
                            values, errors,
                            setFieldValue, validateField,
                        }) => (
                            <form onSubmit={handleSubmit} className="obr-form">
                                <h2 className="obr-heading">
                                    {wall ? "בזמן שאנחנו מכינים את המסגרות," : <>{step === 1 ? "נעים להכיר" : "מעולה"} <span className="obr-emoji">😊</span></>}
                                </h2>
                                <p className="obr-subtext">נתחיל עם פרטים כמו שמך הפרטי ואימייל</p>

                                <div className="obr-fields">
                                    <div className={`obr-input-wrap ${focus.name ? "focused" : ""} ${errors.fullName ? "errored" : ""}`}>
                                        <img
                                            className="obr-input-icon"
                                            src={`../../assets/images/user-square${focus.name ? "-focus" : errors.fullName ? "-error" : ""}.svg`}
                                            alt=""
                                        />
                                        <input
                                            className="obr-input"
                                            id="fullName"
                                            name="fullName"
                                            type="text"
                                            placeholder="מה השם שלך?"
                                            onChange={handleChange}
                                            onFocus={() => setFocus({ name: true })}
                                            onBlur={() => {
                                                validateField('fullName');
                                                setFocus({ name: false });
                                            }}
                                            value={values.fullName}
                                        />
                                    </div>

                                    <div className={`obr-input-wrap ${focus.email ? "focused" : ""} ${errors.email ? "errored" : ""}`}>
                                        <img
                                            className="obr-input-icon"
                                            src={`../../assets/images/onboarding-email${focus.email ? "-focus" : errors.email ? "-error" : ""}.svg`}
                                            alt=""
                                        />
                                        <input
                                            className="obr-input"
                                            id="email"
                                            name="email"
                                            type="email"
                                            placeholder="מה כתובת המייל שלך?"
                                            onChange={handleChange}
                                            onFocus={() => setFocus({ email: true })}
                                            onBlur={() => {
                                                validateField('email');
                                                setFocus({ email: false });
                                            }}
                                            value={values.email}
                                        />
                                    </div>

                                    <div className={`obr-input-wrap ${focus.phone ? "focused" : ""} ${errors.phoneNumber ? "errored" : ""}`}>
                                        <img
                                            className="obr-input-icon"
                                            src={`../../assets/images/onboarding-phone${focus.phone ? "-focus" : errors.phoneNumber ? "-error" : ""}.svg`}
                                            alt=""
                                        />
                                        <input
                                            className="obr-input"
                                            id="phoneNumber"
                                            name="phoneNumber"
                                            type="tel"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            placeholder="מה מספר הטלפון שלך?"
                                            onChange={handleChange}
                                            onFocus={() => setFocus({ phone: true })}
                                            onBlur={() => {
                                                validateField('phoneNumber');
                                                setFocus({ phone: false });
                                            }}
                                            value={values.phoneNumber}
                                            maxLength={10}
                                        />
                                    </div>
                                </div>

                                <label className="obr-checkbox-row" htmlFor="agreed">
                                    <input
                                        className="obr-check"
                                        type="checkbox"
                                        id="agreed"
                                        name="agreed"
                                        checked={values.agreed}
                                        onChange={() => setFieldValue("agreed", !values.agreed)}
                                    />
                                    <span className="obr-check-visual"></span>
                                    <span className="obr-check-label">נא לשלוח לי עדכונים בנוגע להטבות ואירועים</span>
                                </label>

                                <button
                                    className={`obr-submit${checkingGallery ? " obr-submit--busy" : ""}`}
                                    type="submit"
                                    disabled={uploadState || checkingGallery}
                                    aria-busy={checkingGallery}
                                >
                                    {checkingGallery
                                        ? <span className="obr-submit-spinner" aria-label="בודקים…" />
                                        : (wall ? "קחו אותי לקיר התמונות שלי" : "בוחרים תמונות")}
                                </button>
                            </form>
                        )}
                    </Formik>
                </div>

                <div className="obr-footer">
                    <p className="obr-skip" onClick={onSkip}>דלג</p>
                    <div className="obr-progress-track">
                        <div className="obr-progress-fill" style={{ width: `${step * 50}%` }}></div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ThirdOnboardScreen;
